import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Shield, Home } from 'lucide-react'
import { LEGAL, LEGAL_ENTITY_DISPLAY } from '@/lib/legal'

/**
 * Public privacy policy. Identifies the operating business and describes data and
 * SMS handling — referenced during Twilio toll-free verification, so the business
 * identity and SMS section must stay consistent with the Twilio submission and the
 * SMS Consent page.
 */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-muted/30 p-4 py-12">
      <div className="max-w-2xl mx-auto bg-card text-card-foreground rounded-lg border shadow-sm p-8">
        <Shield className="h-12 w-12 text-primary" />
        <h1 className="mt-6 text-3xl font-bold">Privacy Policy</h1>
        <p className="mt-2 text-muted-foreground">
          Effective {LEGAL.effectiveDate}. {LEGAL_ENTITY_DISPLAY}
        </p>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Who we are</h2>
          <p className="text-muted-foreground">
            {LEGAL.product} is operated by {LEGAL.entity} ("we", "us"). {LEGAL.product} lets an event
            organizer collect photos from the people who were there. You can reach us at{' '}
            <a className="text-primary underline" href={`mailto:${LEGAL.supportEmail}`}>
              {LEGAL.supportEmail}
            </a>
            {LEGAL.address ? `, or by mail at ${LEGAL.address}` : ''}.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">What we collect</h2>
          <ul className="list-disc pl-5 text-muted-foreground space-y-1">
            <li><strong>Account data</strong> — name, email, and authentication details for organizers.</li>
            <li><strong>Contact data</strong> — a phone number or email an organizer enters to invite
              someone to a drop, provided the organizer has that person's permission.</li>
            <li><strong>Content</strong> — photos and captions uploaded to a drop.</li>
            <li><strong>Usage data</strong> — basic logs and device/browser information needed to run
              and secure the service.</li>
          </ul>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">How we use it</h2>
          <p className="text-muted-foreground">
            To provide the service: create and manage drops, deliver invitations, store and display
            uploaded photos, process payments, and keep the service secure. We do not sell your
            personal information, and we do not use it for third-party advertising.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Text messages (SMS)</h2>
          <p className="text-muted-foreground">
            The only texts {LEGAL.product} sends are one-to-one invitations to contribute photos to a
            specific drop, plus at most one reminder — each initiated manually by an organizer for a
            person they invited. We send no marketing or bulk messages. Phone numbers used for
            messaging are never sold or shared with third parties or affiliates for their own
            marketing. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help;
            message and data rates may apply. See our{' '}
            <Link className="text-primary underline" to="/sms-consent">SMS Messaging Policy</Link> for
            how consent is obtained.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Sharing</h2>
          <p className="text-muted-foreground">
            We share data only with service providers that help us operate {LEGAL.product} (for
            example hosting, storage, email/SMS delivery, and payment processing), each bound to
            protect it, and when required by law. Photos in a drop are visible to the organizer and
            the people they invite to that drop.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Retention &amp; your choices</h2>
          <p className="text-muted-foreground">
            We keep data for as long as needed to provide the service and as required by law; drop
            content is subject to the drop's retention settings. You can request access to or
            deletion of your personal information by emailing{' '}
            <a className="text-primary underline" href={`mailto:${LEGAL.supportEmail}`}>
              {LEGAL.supportEmail}
            </a>
            .
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Changes</h2>
          <p className="text-muted-foreground">
            We may update this policy; material changes will be reflected by the effective date
            above.
          </p>
        </section>

        <div className="mt-10 flex gap-3">
          <Button asChild variant="outline">
            <Link to="/terms">Terms of Service</Link>
          </Button>
          <Button asChild>
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Back to {LEGAL.product}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
